import classNames from 'classnames';
import { ChevronDown } from 'react-feather';
import { useUID } from 'react-uid';
import { useBlurrableComponentVisibility } from '../../utils/hooks';

interface BaseProps {
  /**
   * The list of options to select from. Should not include the placeholderOption.
   */
  options: string[];
  setOption: (option: string) => void;
  /**
   * Provides a label for the select element. Only accessible to screen readers.
   */
  labelText: string;
  className?: string;
  alignDropdown?: 'left' | 'right';
  required?: boolean;
}

type Props = BaseProps &
  (
    | {
        option: string | undefined;
        /**
         * The option matching this text will be unselectable.
         */
        placeholderOption: string;
      }
    | {
        option: string;
        placeholderOption?: undefined;
      }
  );

const Select: React.FC<Props> = ({
  options,
  option: selectedOption,
  setOption,
  labelText,
  className,
  alignDropdown = 'right',
  required,
  placeholderOption
}) => {
  const { ref, openerRef, isComponentVisible, setIsComponentVisible } = useBlurrableComponentVisibility<HTMLDivElement>(
    false
  );
  const selectId = useUID();
  return (
    <div className={classNames(className, 'relative text-left')}>
      <label htmlFor={selectId} className='sr-only'>
        {labelText}
      </label>
      <select
        id={selectId}
        className='sr-only'
        required={required}
        onChange={e => setOption(e.target.value)}
        value={selectedOption ?? ''}
      >
        {(placeholderOption ? [placeholderOption, ...options] : options).map(option => (
          <option
            key={option}
            value={option !== placeholderOption ? option : ''}
            disabled={option === placeholderOption}
          >
            {option}
          </option>
        ))}
      </select>

      <button
        type='button'
        ref={openerRef}
        className='input-background opener inline-flex items-center justify-between rounded px-4 py-2 w-full h-10 text-sm font-medium'
        aria-hidden='true' /* screen reader users can use the real select element */
        onClick={() => {
          setIsComponentVisible(visible => !visible);
        }}
      >
        {selectedOption ?? placeholderOption}
        <ChevronDown size={18} />
      </button>
      <div
        className={classNames(
          'input-background absolute mt-2 w-48 rounded-md shadow-lg transform duration-100 z-10',
          alignDropdown === 'right' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
          isComponentVisible ? 'scale-100 visible opacity-1' : 'scale-95 invisible opacity-0'
        )}
        aria-hidden='true'
        ref={ref}
      >
        {options.map(option => (
          <button
            className='px-4 py-2 text-sm w-full text-left hover:text-gray-400 touched:text-gray-400'
            type='button'
            key={option}
            onClick={() => {
              setOption(option);
              setIsComponentVisible(false);
            }}
          >
            {option}
          </button>
        ))}
      </div>
      <style jsx>{`
        .opener {
          min-width: 10rem;
        }
      `}</style>
    </div>
  );
};

export default Select;
